import { useMemo } from "react";
import { useSelector } from "react-redux";

const normalizeSubscriptions = (subs) => {
  if (Array.isArray(subs)) return subs;
  if (subs && typeof subs === "object") {
    return Object.values(subs);
  }
  return [];
};

/**
 * Returns the effective teacher/customer identifiers that should be used when
 * fetching teacher-scoped resources. When impersonating another user, the
 * identifiers associated with the impersonated account are returned instead of
 * the admin's defaults.
 */
export default function useEffectiveTeacherIdentity() {
  const becomeUser = useSelector((state) => state.auth.become_user);
  const user = useSelector((state) => state.auth.user);
  const teacherSubscriptionsRaw = useSelector(
    (state) => state.auth.teacher_subscriptions
  );
  const selectedSubscription = useSelector(
    (state) => state.auth.selected_subscriptions
  );

  const teacherSubscriptions = useMemo(
    () => normalizeSubscriptions(teacherSubscriptionsRaw),
    [teacherSubscriptionsRaw]
  );

  return useMemo(() => {
    let teacherId = user?.user_id ?? null;
    let customerId = user?.customer_id ?? null;

    const subscriptionCustomerId = selectedSubscription?.customer_id;
    const subscriptionTeacherId =
      selectedSubscription?.customer_user_id ??
      selectedSubscription?.user_id ??
      null;

    if (becomeUser) {
      // Prefer identifiers from the selected subscription when impersonating.
      if (subscriptionCustomerId) {
        customerId = subscriptionCustomerId;
      }
      if (subscriptionTeacherId) {
        teacherId = subscriptionTeacherId;
      }

      // Fall back to the first available teacher subscription.
      if (teacherSubscriptions.length > 0) {
        const first = teacherSubscriptions[0];
        customerId = customerId ?? first?.customer_id ?? null;
        teacherId =
          teacherId ??
          first?.user_id ??
          first?.customer_user_id ??
          null;
      }
    } else {
      // When not impersonating, still allow the selected subscription to
      // override the customer if one is chosen.
      if (!customerId && subscriptionCustomerId) {
        customerId = subscriptionCustomerId;
      }
    }

    return {
      teacherId: teacherId ?? null,
      customerId: customerId ?? null,
    };
  }, [
    becomeUser,
    user?.user_id,
    user?.customer_id,
    teacherSubscriptions,
    selectedSubscription?.customer_id,
    selectedSubscription?.customer_user_id,
    selectedSubscription?.user_id,
  ]);
}
